import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PopupDisplay from "@/components/PopupDisplay";
import { Plus, Plane, Upload, Trash2, FileText, Clock, Building2, AlertCircle, Download, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { formatLocalTime } from "@/utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to parse date strings in multiple formats
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  
  // Check if DD/MM/YYYY format
  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/');
    return new Date(year, parseInt(month) - 1, day);
  }
  
  // Check if YYYY-MM-DD format or other standard formats
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  return null;
};

const Flights = () => {
  const [flights, setFlights] = useState([]);
  const [filteredFlights, setFilteredFlights] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [services, setServices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [editingFlight, setEditingFlight] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [showImportInstructions, setShowImportInstructions] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [showGDPRPopup, setShowGDPRPopup] = useState(false);
  const [showServiceHoursPopup, setShowServiceHoursPopup] = useState(false);
  const [showPendingApprovalPopup, setShowPendingApprovalPopup] = useState(false);
  const [pendingFlightForService, setPendingFlightForService] = useState(null);
  const [pendingFlightDepartureAirport, setPendingFlightDepartureAirport] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingFlightId, setPendingFlightId] = useState(null);
  const [companyEmail, setCompanyEmail] = useState('accounting@viphandling.com');
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAgency = currentUser.role === 'agency';
  const isStaff = currentUser.role === 'staff';
  const isAdmin = currentUser.role === 'admin';
  
  // Load user permissions
  const userPermissions = JSON.parse(localStorage.getItem('userPermissions') || '{}');
  const canViewFlights = isAdmin || userPermissions.flights_view === true;
  const canEditFlights = isAdmin || isAgency || userPermissions.flights_edit === true;
  const canCreateFlights = isAdmin || isAgency || userPermissions.flights_create === true;
  const canSaveFlights = isAdmin || userPermissions.flights_save === true;
  const canDeleteFlights = isAdmin || userPermissions.flights_save === true; // Using save permission for delete
  
  // Filter states - default to upcoming flights with all months
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState("all"); // Changed to "all" for upcoming flights filter
  const [selectedAgency, setSelectedAgency] = useState("all");
  const [dateFilter, setDateFilter] = useState("upcoming"); // "all", "past", "upcoming"
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" = nearest first (default), "desc" = newest first
  
  // Helper function to convert input to uppercase for specific fields
  const handleUppercaseInput = (field, value) => {
    const uppercaseFields = ['flight_prefix', 'flight_number', 'departure_airport', 'arrival_airport', 'pnr_list', 'airline'];
    const newValue = uppercaseFields.includes(field) ? value.toUpperCase() : value;
    setFormData({ ...formData, [field]: newValue });
  };
  
  const [formData, setFormData] = useState({
    flight_number: "",
    flight_prefix: "",
    flight_number_only: "",
    flight_date: "",
    flight_time: "",
    departure_airport: "",
    arrival_airport: "",
    airline: "",
    pnr_list: "",
    flight_type: "departure",  // Default to departure
    passenger_count: "",
    passenger_names: "",
    special_requests: "",
    group_name: "",
    tour_leader_name: "",
    tour_leader_phone: "",
    agency_emergency_contact: "",
    agency_id: "",
    agent_name: ""
  });
  const [prefixConfirmDialog, setPrefixConfirmDialog] = useState({ open: false, matches: [] });
  const [serviceFormData, setServiceFormData] = useState({
    service_id: "",
    price: "",
    parking_fee: "",
    notes: ""
  });
  const [serviceHoursSelection, setServiceHoursSelection] = useState(null); // null, 2, or 3
  const [currentBasicAssistanceService, setCurrentBasicAssistanceService] = useState(null);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceOptions, setPriceOptions] = useState([]);

  useEffect(() => {
    fetchData();
    fetchCompanySettings();
  }, []);

  // Check URL parameters for edit mode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editFlightId = urlParams.get('edit');
    
    if (editFlightId && flights.length > 0) {
      const flightToEdit = flights.find(f => f.id === editFlightId);
      if (flightToEdit) {
        handleEditFlight(flightToEdit);
        // Clear URL parameter after loading
        window.history.replaceState({}, '', '/flights');
      }
    }
  }, [flights]);

  
  // Check if we need to open service dialog from Planning page
  useEffect(() => {
    const openServiceData = localStorage.getItem('openServiceDialog');
    if (openServiceData && flights.length > 0) {
      try {
        const { flightId, flightNumber } = JSON.parse(openServiceData);
        
        // Find the flight in the loaded flights
        const flight = flights.find(f => f.id === flightId);
        
        if (flight) {
          // Set the selected flight
          setSelectedFlight(flight);
          
          // Open the service dialog
          setServiceDialogOpen(true);
          
          // Clear the localStorage flag
          localStorage.removeItem('openServiceDialog');
          
          toast.success(`Ready to add services to ${flightNumber}`);
        }
      } catch (error) {
        console.error('Failed to parse openServiceDialog data', error);
        localStorage.removeItem('openServiceDialog');
      }
    }
  }, [flights]);

  
  useEffect(() => {
    applyFilters();
  }, [flights, selectedYear, selectedMonth, selectedAgency, dateFilter, sortOrder]);
  
  // Auto-set agency_id for agency users when dialog opens
  useEffect(() => {
    if (open && isAgency && currentUser.agency_id && !editingFlight) {
      // For new flights, auto-set agency_id
      setFormData(prev => ({
        ...prev,
        agency_id: currentUser.agency_id
      }));
    }
  }, [open, isAgency, currentUser.agency_id, editingFlight]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [flightsRes, agenciesRes, servicesRes, agentsRes] = await Promise.all([
        axios.get(`${API}/flights`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agencies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agents`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFlights(flightsRes.data);
      setAgencies(agenciesRes.data);
      setServices(servicesRes.data);
      setAgents(agentsRes.data); // Now contains agents with role field
    } catch (error) {
      toast.error("Failed to fetch data");
    }
    setLoading(false);
  };
  
  const fetchCompanySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.email) {
        setCompanyEmail(response.data.email);
      }
    } catch (error) {
      console.error("Failed to fetch company settings");
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // If already in DD/MM/YYYY format, return as-is
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateString;
    }
    
    try {
      // Handle YYYY-MM-DD format
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      return dateString;
    } catch {
      return dateString;
    }
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      // If time includes seconds (HH:MM:SS), remove them
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
      return timeString;
    } catch {
      return timeString;
    }
  };
  
  const canModifyService = (flight) => {
    // INVOICE LOCK: Nobody can modify services when flight has ANY invoice
    // This applies to ALL users including Admin
    if (flight.invoiced || flight.invoice_status || flight.invoice_id || flight.invoice_number) {
      return false;
    }
    
    if (!isAgency) return true; // Admin can modify (unless invoiced - checked above)
    
    // For agencies: Check if flight is more than 24 hours away
    const flightDate = parseDateString(flight.flight_date);
    if (!flightDate) return false;
    
    const timeStr = flight.flight_time || '00:00';
    flightDate.setHours(parseInt(timeStr.split(':')[0]), parseInt(timeStr.split(':')[1]));
    
    const now = new Date();
    const hoursDiff = (flightDate - now) / (1000 * 60 * 60);
    
    return hoursDiff >= 24;
  };

  
  const lookupAirlineByFlightNumber = async (flightNumber) => {
    if (!flightNumber || flightNumber.length < 2) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/airline-codes/lookup/${flightNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.airline_name) {
        setFormData(prev => ({ ...prev, airline: response.data.airline_name }));
      }
    } catch (error) {
      console.error("Failed to lookup airline:", error);
    }
  };

  const validateFlightPrefix = async (prefix) => {
    if (!prefix || prefix.length < 2) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/flights/validate-prefix/${prefix}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_ambiguity && response.data.matches.length > 1) {
        // Show confirmation dialog
        setPrefixConfirmDialog({
          open: true,
          matches: response.data.matches
        });
      }
    } catch (error) {
      console.error("Failed to validate prefix:", error);
    }
  };
  
  const handlePrefixConfirm = (selectedMatch) => {
    setFormData(prev => ({
      ...prev,
      flight_prefix: selectedMatch.prefix,
      airline: selectedMatch.airline,
      flight_number: selectedMatch.prefix + prev.flight_number_only
    }));
    setPrefixConfirmDialog({ open: false, matches: [] });
  };

  
  const applyFilters = () => {
    let filtered = [...flights];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of today
    
    // STAFF FILTER: Staff users only see flights assigned to them
    if (isStaff && currentUser.id) {
      // Find agent record for this staff user
      const staffAgent = agents.find(agent => agent.user_id === currentUser.id);
      if (staffAgent) {
        // Filter by agent NAME - check both agent_name (single) and agent_names (array) for multi-agent support
        filtered = filtered.filter(flight => {
          // Check single agent assignment (backward compatibility)
          if (flight.agent_name === staffAgent.name) return true;
          // Check multi-agent assignment array
          if (flight.agent_names && Array.isArray(flight.agent_names) && flight.agent_names.includes(staffAgent.name)) return true;
          return false;
        });
      } else {
        // If no agent record found, show no flights
        filtered = [];
      }
    }
    
    // AGENCY FILTER: Agency users only see their agency's flights
    if (isAgency && currentUser.agency_id) {
      filtered = filtered.filter(flight => flight.agency_id === currentUser.agency_id);
    }
    
    // Apply Date Range filter (upcoming, past, all)
    if (dateFilter === "upcoming") {
      filtered = filtered.filter(flight => {
        const flightDate = parseDateString(flight.flight_date);
        if (!flightDate) return false;
        flightDate.setHours(0, 0, 0, 0);
        return flightDate >= today;
      });
    } else if (dateFilter === "past") {
      filtered = filtered.filter(flight => {
        const flightDate = parseDateString(flight.flight_date);
        if (!flightDate) return false;
        flightDate.setHours(0, 0, 0, 0);
        return flightDate < today;
      });
    }
    // If "all", no date filter applied
    
    // Apply Year and Month filters
    filtered = filtered.filter(flight => {
      const flightDate = parseDateString(flight.flight_date);
      if (!flightDate) return false;
      
      const flightYear = flightDate.getFullYear();
      const flightMonth = flightDate.getMonth() + 1;
      
      // Year filter
      if (selectedYear !== "all" && flightYear.toString() !== selectedYear) {
        return false;
      }
      
      // Month filter
      if (selectedMonth !== "all" && flightMonth.toString() !== selectedMonth) {
        return false;
      }
      
      return true;
    });
    
    // Apply Agency filter (for admin/operations users)
    if (!isAgency && !isStaff && selectedAgency !== "all") {
      filtered = filtered.filter(f => {
        // Safety check: only filter if flight has valid agency_id
        if (!f || !f.agency_id) return false;
        return f.agency_id === selectedAgency;
      });
    }

    // Sort by date first, then by time when dates are the same
    filtered.sort((a, b) => {
      const dateA = parseDateString(a.flight_date);
      const dateB = parseDateString(b.flight_date);
      
      // Handle invalid dates - push them to the end
      if (!dateA || !dateB) {
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
      }
      
      // Compare dates only (ignore time component) by comparing year, month, day
      const yearA = dateA.getFullYear();
      const yearB = dateB.getFullYear();
      const monthA = dateA.getMonth();
      const monthB = dateB.getMonth();
      const dayA = dateA.getDate();
      const dayB = dateB.getDate();
      
      // Compare year first
      if (yearA !== yearB) {
        return sortOrder === "desc" ? yearB - yearA : yearA - yearB;
      }
      // Then month
      if (monthA !== monthB) {
        return sortOrder === "desc" ? monthB - monthA : monthA - monthB;
      }
      // Then day
      if (dayA !== dayB) {
        return sortOrder === "desc" ? dayB - dayA : dayA - dayB;
      }
      
      // Dates are the same - sort by flight time
      const timeA = a.flight_time || '00:00';
      const timeB = b.flight_time || '00:00';
      return sortOrder === "desc" ? timeB.localeCompare(timeA) : timeA.localeCompare(timeB);
    });
    
    setFilteredFlights(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check 24-hour rule for agency users editing flights
    if (isAgency && editingFlight) {
      const flight = flights.find(f => f.id === editingFlight);
      if (flight) {
        const flightDate = parseDateString(flight.flight_date);
        if (flightDate && flight.flight_time) {
          const [hours, minutes] = flight.flight_time.split(':');
          flightDate.setHours(parseInt(hours) || 0, parseInt(minutes) || 0);
        }
        const now = new Date();
        const hoursDiff = (flightDate - now) / (1000 * 60 * 60);
        
        if (hoursDiff < 24) {
          setOpen(false);
          setShowContactPopup(true);
          return;
        }
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      const pnrArray = formData.pnr_list.split(',').map(p => p.trim()).filter(p => p);
      const passengerNames = formData.passenger_names.split('\n').map(name => name.trim()).filter(name => name);
      
      const flightData = {
        ...formData,
        passenger_count: parseInt(formData.passenger_count),
        pnr_list: pnrArray,
        passenger_names: passengerNames
      };

      if (editingFlight) {
        await axios.put(`${API}/flights/${editingFlight}`, flightData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Flight updated successfully");
      } else {
        const response = await axios.post(`${API}/flights`, flightData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Show 2h/3h service selection popup for ALL users (agency/admin/staff)
        if (response.data.id) {
          // Store departure airport for popup logic
          setPendingFlightDepartureAirport(flightData.departure_airport);
          setPendingFlightForService(response.data.id);
          
          // For agency users, show pending approval notification first
          if (isAgency) {
            setShowPendingApprovalPopup(true);
            
            // Fetch agency settings to determine popup behavior
            const agencyResponse = await axios.get(`${API}/agencies/${currentUser.agency_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const agency = agencyResponse.data;
            
            // Check which services the agency has access to
            const has2h = agency.has_2h_assistance !== false; // Default true
            const has3h = agency.has_3h_assistance !== false; // Default true
            
            if (has2h && has3h) {
              // Both options available - show popup
              setShowServiceHoursPopup(true);
            } else if (has2h && !has3h) {
              // Only 2h - auto-add without popup
              await handleServiceHoursSelection(2);
            } else if (!has2h && has3h) {
              // Only 3h - auto-add without popup
              await handleServiceHoursSelection(3);
            } else {
              // Neither option - don't show popup, just refresh
              fetchData();
            }
          } else {
            // For admin/staff users, always show service hours popup
            setShowServiceHoursPopup(true);
          }
        }
        
        toast.success("Flight created successfully");
      }
      
      setOpen(false);
      setEditingFlight(null);
      setFormData({
        flight_number: "", flight_date: "", flight_time: "", departure_airport: "", arrival_airport: "",
        airline: "", pnr_list: "", flight_type: "departure", passenger_count: "", passenger_names: "",
        special_requests: "", agency_id: "", agent_name: ""
      });
      
      // Fetch data if not showing service hours popup
      if (editingFlight) {
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingFlight ? 'update' : 'create'} flight`);
    }
  };

  const handleRefreshPrice = async (flightId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Step 1: Get available price options
      const optionsResponse = await axios.get(
        `${API}/flights/${flightId}/available-prices`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const options = optionsResponse.data.available_options || [];
      
      // If multiple options available, show choice dialog
      if (options.length > 1) {
        setPriceOptions(options);
        setSelectedFlight(flightId);
        setPriceDialogOpen(true);
      } else if (options.length === 1) {
        // Only one option, refresh directly
        await refreshWithService(flightId, options[0].service_id);
      } else {
        // No options available
        toast.error('No pricing available for this flight');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check prices');
    }
  };
  
  const refreshWithService = async (flightId, serviceId = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/flights/${flightId}/refresh-price${serviceId ? `?selected_service_id=${serviceId}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Close dialog immediately
      setPriceDialogOpen(false);
      
      // Show success message with safe number handling
      const totalAmount = response.data?.total_amount || 0;
      toast.success(`Price refreshed: €${Number(totalAmount).toFixed(2)}`);
      
      // Force reload flights to show updated price immediately
      await fetchFlights();
      
      // Also trigger a re-render by updating the filtered flights
      applyFilters();
      
    } catch (error) {
      console.error('Refresh price error:', error);
      toast.error(error.response?.data?.detail || 'Failed to refresh price');
      setPriceDialogOpen(false);
    }
  };

  const handleEditFlight = (flight) => {
    setEditingFlight(flight.id);
    
    // Extract prefix and number from flight_number
    const flightNum = flight.flight_number || "";
    const prefix = flight.flight_prefix || flightNum.match(/^[A-Z]{2}/)?.[0] || "";
    const numberOnly = flight.flight_number_only || flightNum.replace(prefix, "");
    
    // Convert flight_date to YYYY-MM-DD format for date input
    let formattedDate = "";
    if (flight.flight_date) {
      // Check if already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}/.test(flight.flight_date)) {
        formattedDate = flight.flight_date.substring(0, 10);
      } else if (/^\d{2}\/\d{2}\/\d{4}/.test(flight.flight_date)) {
        // Convert DD/MM/YYYY to YYYY-MM-DD
        const parts = flight.flight_date.split('/');
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    
    setFormData({
      flight_number: flight.flight_number,
      flight_prefix: prefix,
      flight_number_only: numberOnly,
      flight_date: formattedDate,
      flight_time: flight.flight_time || "",
      departure_airport: flight.departure_airport || "",
      arrival_airport: flight.arrival_airport || "",
      airline: flight.airline || "",
      pnr_list: flight.pnr_list?.join(', ') || "",
      flight_type: flight.flight_type,
      passenger_count: flight.passenger_count?.toString() || "",
      passenger_names: flight.passenger_names?.join('\n') || "",
      special_requests: flight.special_requests || "",
      group_name: flight.group_name || "",
      tour_leader_name: flight.tour_leader_name || "",
      tour_leader_phone: flight.tour_leader_phone || "",
      agency_emergency_contact: flight.agency_emergency_contact || "",
      agency_id: isAgency ? currentUser.agency_id : flight.agency_id,  // Force agency user's own agency
      agent_name: flight.agent_name || ""
    });
    
    // Detect current service hours (2h or 3h) from Basic Assistance service
    const basicAssistanceService = flight.services?.find(s => 
      s.is_basic_assistance || 
      (s.type && s.type.toLowerCase().includes('basic assistance')) ||
      (s.service_name && s.service_name.toLowerCase().includes('basic assistance'))
    );
    if (basicAssistanceService) {
      setCurrentBasicAssistanceService(basicAssistanceService);
      // Check if service name contains 2h or 3h
      if (basicAssistanceService.service_name && basicAssistanceService.service_name.match(/2\s*h/i)) {
        setServiceHoursSelection(2);
      } else if (basicAssistanceService.service_name && basicAssistanceService.service_name.match(/3\s*h/i)) {
        setServiceHoursSelection(3);
      } else {
        setServiceHoursSelection(null);
      }
    } else {
      setCurrentBasicAssistanceService(null);
      setServiceHoursSelection(null);
    }
    
    setOpen(true);
  };

  // Check if flight is locked (invoice status and date)
  // INVOICE LOCK RULE: Flight is LOCKED only when invoice_status is "draft" or "approved"
  // Flight is UNLOCKED when invoice_status is "credit_note" or "cancelled"
  const isFlightLocked = (flight) => {
    // If invoice_status is "credit_note" or "cancelled" - flight is NOT locked
    if (flight.invoice_status === 'credit_note' || flight.invoice_status === 'cancelled') {
      return false;
    }
    
    // If flight has a credit note number (starts with NC) and invoiced=false, it's NOT locked
    if (flight.invoice_number && flight.invoice_number.startsWith('NC') && !flight.invoiced) {
      return false;
    }
    
    // Lock if flight has an active invoice (draft or approved status)
    if (flight.invoiced || flight.invoice_status === 'draft' || flight.invoice_status === 'approved' || flight.invoice_status === 'sent') {
      return true;
    }
    
    // Lock if flight has invoice_id pointing to an active invoice
    if (flight.invoice_id && flight.invoice_status && flight.invoice_status !== 'credit_note' && flight.invoice_status !== 'cancelled') {
      return true;
    }
    
    // Check date lock (before November 1, 2025 - hide October 2025 and earlier)
    if (flight.flight_date) {
      try {
        const flightDate = parseDateString(flight.flight_date);
        const lockDate = new Date('2025-11-01');
        if (flightDate && flightDate < lockDate) {
          return true;
        }
      } catch (e) {
        // If date parsing fails, don't lock
      }
    }
    
    return false;
  };

  // Check if flight can be DELETED
  // Flight can be deleted if it has no active invoice (credit_note/cancelled status is OK)
  const canDeleteFlight = (flight) => {
    // If invoice_status is "credit_note" or "cancelled" - flight CAN be deleted
    if (flight.invoice_status === 'credit_note' || flight.invoice_status === 'cancelled') {
      return true;
    }
    
    // If flight has credit note number and invoiced=false, it CAN be deleted
    if (flight.invoice_number && flight.invoice_number.startsWith('NC') && !flight.invoiced) {
      return true;
    }
    
    // Cannot delete if flight has an active invoice
    if (flight.invoiced || flight.invoice_status === 'draft' || flight.invoice_status === 'approved' || flight.invoice_status === 'sent') {
      return false;
    }
    
    if (flight.invoice_id && flight.invoice_status && flight.invoice_status !== 'credit_note' && flight.invoice_status !== 'cancelled') {
      return false;
    }
    
    return true;
  };

  const handleServiceHoursChange = async (newHours) => {
    if (!editingFlight || !currentBasicAssistanceService) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Update basic assistance service hours (uses new endpoint that properly updates pricing)
      const updateResponse = await axios.put(
        `${API}/flights/${editingFlight}/update-basic-assistance-hours`,
        { duty_hours: newHours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Fetch updated flight to get new service details with correct price
      const updatedFlightRes = await axios.get(`${API}/flights/${editingFlight}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedFlight = updatedFlightRes.data;
      
      // Update current basic assistance service with new price
      const newBasicAssistanceService = updatedFlight.services?.find(s => s.is_basic_assistance);
      if (newBasicAssistanceService) {
        setCurrentBasicAssistanceService(newBasicAssistanceService);
      }
      
      setServiceHoursSelection(newHours);
      
      const price = newBasicAssistanceService?.price || 0;
      const parkingFee = newBasicAssistanceService?.parking_fee || 0;
      const total = price + parkingFee;
      
      const message = parkingFee > 0 
        ? `Service updated to ${newHours}h - Price: €${price.toFixed(2)} + Parking: €${parkingFee.toFixed(2)} = €${total.toFixed(2)}`
        : `Service updated to ${newHours}h - Price: €${price.toFixed(2)}`;
      
      toast.success(message);
      
      // Refresh all data to update the flights list
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to update service hours";
      toast.error(errorMessage);
      console.error("Service hours update error:", error);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    
    // Check 24-hour rule for agency users
    if (isAgency && selectedFlight) {
      const flightDate = parseDateString(selectedFlight.flight_date);
      if (flightDate && selectedFlight.flight_time) {
        const [hours, minutes] = selectedFlight.flight_time.split(':');
        flightDate.setHours(parseInt(hours) || 0, parseInt(minutes) || 0);
      }
      const now = new Date();
      const hoursDiff = (flightDate - now) / (1000 * 60 * 60);
      
      if (hoursDiff < 24) {
        setServiceDialogOpen(false);
        setShowContactPopup(true);
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      const service = services.find(s => s.id === serviceFormData.service_id);
      
      await axios.post(`${API}/flights/${selectedFlight.id}/services`, {
        service_id: serviceFormData.service_id,
        service_name: service.name,
        price: parseFloat(serviceFormData.price),
        parking_fee: parseFloat(serviceFormData.parking_fee || 0),
        notes: serviceFormData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Service added to flight");
      setServiceDialogOpen(false);
      setServiceFormData({ service_id: "", price: "", parking_fee: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add service");
    }
  };

  const handleServiceHoursSelection = async (hours) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/flights/${pendingFlightForService}/auto-add-basic-assistance`,
        { duty_hours: hours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${hours}h Basic Assistance service added`);
      setShowServiceHoursPopup(false);
      setPendingFlightForService(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to add Basic Assistance service");
      setShowServiceHoursPopup(false);
      setPendingFlightForService(null);
      fetchData(); // Still refresh to show the created flight
    }
  };


  const handleFileUpload = async (flightId, file) => {
    const flight = flights.find(f => f.id === flightId);
    
    // Check 24-hour rule for agency users
    if (isAgency && flight) {
      const flightDate = parseDateString(flight.flight_date);
      if (flightDate && flight.flight_time) {
        const [hours, minutes] = flight.flight_time.split(':');
        flightDate.setHours(parseInt(hours) || 0, parseInt(minutes) || 0);
      }
      const now = new Date();
      const hoursDiff = (flightDate - now) / (1000 * 60 * 60);
      
      if (hoursDiff < 24) {
        setShowContactPopup(true);
        return;
      }
      
      // Show GDPR popup
      setPendingFile(file);
      setPendingFlightId(flightId);
      setShowGDPRPopup(true);
      return;
    }
    
    // Admin users upload directly
    await uploadFile(flightId, file);
  };
  
  const uploadFile = async (flightId, file) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/flights/${flightId}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success("File uploaded successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload file");
    }
  };
  
  const handleGDPRAccept = async () => {
    setShowGDPRPopup(false);
    await uploadFile(pendingFlightId, pendingFile);
    setPendingFile(null);
    setPendingFlightId(null);
  };
  
  const handleGDPRCancel = () => {
    setShowGDPRPopup(false);
    setPendingFile(null);
    setPendingFlightId(null);
  };

  const handleDeleteFlight = async (id) => {
    // Check 24-hour rule for agency users
    if (isAgency) {
      const flight = flights.find(f => f.id === id);
      if (flight) {
        const flightDate = parseDateString(flight.flight_date);
        if (flightDate && flight.flight_time) {
          const [hours, minutes] = flight.flight_time.split(':');
          flightDate.setHours(parseInt(hours) || 0, parseInt(minutes) || 0);
        }
        const now = new Date();
        const hoursDiff = (flightDate - now) / (1000 * 60 * 60);
        
        if (hoursDiff < 24) {
          setShowContactPopup(true);
          return;
        }
      }
    }
    
    if (!window.confirm('Are you sure you want to delete this flight?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/flights/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Flight deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete flight");
    }
  };

  const handleRemoveService = async (flightId, serviceId) => {
    const flight = flights.find(f => f.id === flightId);
    
    // Check 24-hour rule for agency users
    if (!canModifyService(flight)) {
      setShowContactPopup(true);
      return;
    }
    
    if (!window.confirm('Remove this service from the flight?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/flights/${flightId}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Service removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove service");
    }
  };

  const handleDownloadFile = async (flightId, fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/flights/${flightId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        console.error('Empty file received');
        toast.error("File is empty or not found");
        return;
      }
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("File downloaded successfully");
    } catch (error) {
      console.error('Download error:', error);
      let errorMsg = "Failed to download file";
      // When responseType is 'blob', error response data is also a Blob - parse it
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        } catch (e) { /* ignore parse errors */ }
      } else {
        errorMsg = error.response?.data?.detail || error.message || errorMsg;
      }
      toast.error(errorMsg);
    }
  };

  const handleDeleteFile = async (flightId, fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/flights/${flightId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("File deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error("Please select a file");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API}/flights/import-excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.log("Import errors:", response.data.errors);
      }
      setImportDialogOpen(false);
      setImportFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import Excel file");
    }
  };


  const handleDownloadTemplate = () => {
    // Create CSV template with correct format (Excel compatible)
    const csvContent = [
      // Header row
      ['Flight Number', 'Flight Date', 'Flight Time', 'Departure Airport', 'Arrival Airport', 'PNR List', 'Flight Type', 'Passenger Count', 'Special Requests', '2h Service', '3h Service'],
      // Sample rows
      ['SN1234', '2024-12-25', '14:30', 'BRU', 'LHR', 'ABC123, DEF456', 'departure', '2', 'Wheelchair assistance', 'x', ''],
      ['BA5678', '2024-12-26', '09:15', 'LHR', 'BRU', 'GHI789', 'arrival', '1', 'VIP service', '', 'x']
    ];
    
    // Convert to CSV format
    const csv = csvContent.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    // Create blob with proper CSV format
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'flight_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded! Open with Excel");
  };


  const handleExportPassengerList = async (flightId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/flights/${flightId}/passenger-list-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Passenger_List_${flightId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Passenger list exported");
    } catch (error) {
      toast.error("Failed to export passenger list");
    }
  };

  if (loading) return <div style={{ color: '#B0B0B0' }}>Loading...</div>;

  return (
    <div data-testid="flights-page">
      {/* Popup Display */}
      <PopupDisplay location="flights" />
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Flight Management</h1>
          <p style={{ color: '#B0B0B0' }}>Manage flights and services</p>
        </div>
        <div className="flex gap-2">
          {(isAdmin || canCreateFlights) && !isStaff && (
            <Button 
              onClick={() => setShowImportInstructions(true)}
              style={{ background: '#4A5A8A', color: '#FFFFFF' }}
            >
              <FileText className="mr-2 h-4 w-4" />Import Excel
            </Button>
          )}

          {/* Import Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
              <DialogHeader>
                <DialogTitle style={{ color: '#FFFFFF' }}>Import Flights from Excel</DialogTitle>
                <DialogDescription style={{ color: '#B0B0B0' }}>Upload an Excel file to import flights</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Select File (.xlsx, .xls, .csv)</Label>
                  <Input 
                    type="file" 
                    accept=".xlsx,.xls,.csv" 
                    onChange={(e) => setImportFile(e.target.files[0])}
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button 
                    type="button" 
                    onClick={() => {
                      setImportDialogOpen(false);
                      setImportFile(null);
                    }}
                    style={{ background: '#4A5A8A', color: '#FFFFFF' }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleImportExcel}
                    style={{ background: '#FFFFFF', color: '#0A1128' }}
                  >
                    Import
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          {(isAdmin || canCreateFlights) && !isStaff && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => {
                    setEditingFlight(null);
                    setFormData({
                      flight_number: "",
                      flight_prefix: "",
                      flight_number_only: "",
                      flight_date: "",
                      flight_time: "",
                      departure_airport: "",
                      arrival_airport: "",
                      airline: "",
                      pnr_list: "",
                      flight_type: "departure",
                      passenger_count: "",
                      passenger_names: "",
                      special_requests: "",
                      group_name: "",
                      tour_leader_name: "",
                      tour_leader_phone: "",
                      agency_emergency_contact: "",
                      agency_id: isAgency ? currentUser.agency_id : "",
                      agent_name: ""
                    });
                  }}
                  style={{ background: '#FFFFFF', color: '#0A1128' }} 
                  data-testid="add-flight-button"
                >
                  <Plus className="mr-2 h-4 w-4" />Add Flight
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#FFFFFF' }}>
                {editingFlight ? "✏️ Editing Flight" : "➕ Add Flight"}
              </DialogTitle>
              <DialogDescription style={{ color: '#B0B0B0' }}>
                {editingFlight ? "Update flight information below" : "Create a new flight record"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Flight Prefix, Number, and Airline - All on one line */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Flight Prefix *</Label>
                  <Input 
                    value={formData.flight_prefix} 
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      handleUppercaseInput('flight_prefix', value);
                      setFormData(prev => ({ ...prev, flight_number: value + formData.flight_number_only }));
                    }}
                    onBlur={() => validateFlightPrefix(formData.flight_prefix)}
                    placeholder="SN, 5O"
                    maxLength="3"
                    required 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Airline code</p>
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Flight Number *</Label>
                  <Input 
                    value={formData.flight_number_only} 
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, flight_number_only: value, flight_number: formData.flight_prefix + value });
                      // Auto-lookup airline when complete
                      if (formData.flight_prefix && value.length >= 1) {
                        lookupAirlineByFlightNumber(formData.flight_prefix + value);
                      }
                    }} 
                    placeholder="1234"
                    required 
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Number only</p>
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Airline <span className="text-xs" style={{ color: '#B0B0B0' }}>(Auto)</span></Label>
                  <Input 
                    value={formData.airline} 
                    onChange={(e) => handleUppercaseInput('airline', e.target.value)} 
                    placeholder="Auto-filled"
                    disabled={isAgency && formData.airline}
                    style={{ 
                      background: (isAgency && formData.airline) ? '#0F1623' : '#0A1128', 
                      borderColor: '#4A5A8A', 
                      color: (isAgency && formData.airline) ? '#808080' : '#E8E8E8',
                      cursor: (isAgency && formData.airline) ? 'not-allowed' : 'text'
                    }} 
                  />
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Auto-recognized</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Flight Date *</Label>
                  <Input 
                    type="date" 
                    value={formData.flight_date} 
                    onChange={(e) => setFormData({ ...formData, flight_date: e.target.value })} 
                    required 
                    style={{ 
                      background: '#0A1128', 
                      borderColor: '#4A5A8A', 
                      color: '#E8E8E8',
                      colorScheme: 'dark'
                    }} 
                  />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Flight Time *</Label>
                  <Input type="time" value={formData.flight_time} onChange={(e) => setFormData({ ...formData, flight_time: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Departure Airport *</Label>
                  <Input placeholder="BRU" value={formData.departure_airport} onChange={(e) => handleUppercaseInput('departure_airport', e.target.value)} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Arrival Airport *</Label>
                  <Input placeholder="CDG" value={formData.arrival_airport} onChange={(e) => handleUppercaseInput('arrival_airport', e.target.value)} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Flight Type *</Label>
                  <Select value={formData.flight_type} onValueChange={(value) => setFormData({ ...formData, flight_type: value })}>
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      <SelectItem value="arrival">Arrival</SelectItem>
                      <SelectItem value="departure">Departure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>PNR Numbers (comma separated)</Label>
                <Input placeholder="ABC123, DEF456" value={formData.pnr_list} onChange={(e) => handleUppercaseInput('pnr_list', e.target.value)} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Passenger Count *</Label>
                  <Input type="number" value={formData.passenger_count} onChange={(e) => setFormData({ ...formData, passenger_count: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Travel Agency *</Label>
                  <Select 
                    value={formData.agency_id} 
                    onValueChange={(value) => setFormData({ ...formData, agency_id: value })}
                    disabled={isAgency}
                  >
                    <SelectTrigger 
                      style={{ 
                        background: isAgency ? '#0F1623' : '#0A1128', 
                        borderColor: '#4A5A8A', 
                        color: isAgency ? '#808080' : '#E8E8E8',
                        cursor: isAgency ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <SelectValue placeholder="Select agency" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      {agencies.filter(a => a.is_active !== false).sort((a, b) => a.name.localeCompare(b.name)).map((agency) => (
                        <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!isAgency && (
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Assign Agent</Label>
                  <Select value={formData.agent_name} onValueChange={(value) => setFormData({ ...formData, agent_name: value })}>
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      {agents.sort((a, b) => a.name.localeCompare(b.name)).map((agent) => (
                        <SelectItem key={agent.id} value={agent.name}>
                          {agent.name} {agent.role && `(${agent.role})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label style={{ color: '#E8E8E8' }}>Passenger Names (one per line)</Label>
                <Textarea placeholder="John Doe\nJane Smith" value={formData.passenger_names} onChange={(e) => setFormData({ ...formData, passenger_names: e.target.value })} rows={4} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Special Requests</Label>
                <Textarea value={formData.special_requests} onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              
              {/* NEW CONTACT FIELDS - Task 2 */}
              <div className="mt-4 p-4 rounded" style={{ background: 'rgba(34, 197, 94, 0.05)', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                <div className="mb-3">
                  <Label style={{ color: '#22C55E', fontWeight: 'bold' }}>Group & Contact Information</Label>
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Essential contact details for tour groups</p>
                </div>
                <div className="mb-3">
                  <Label style={{ color: '#E8E8E8' }}>Group Name</Label>
                  <Input 
                    value={formData.group_name} 
                    onChange={(e) => setFormData({ ...formData, group_name: e.target.value })} 
                    placeholder="e.g., Senior Citizens Tour Group"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label style={{ color: '#E8E8E8' }}>Tour Leader Name</Label>
                    <Input 
                      value={formData.tour_leader_name} 
                      onChange={(e) => setFormData({ ...formData, tour_leader_name: e.target.value })} 
                      placeholder="John Smith"
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#E8E8E8' }}>Tour Leader Phone</Label>
                    <Input 
                      value={formData.tour_leader_phone} 
                      onChange={(e) => setFormData({ ...formData, tour_leader_phone: e.target.value })} 
                      placeholder="+32 123 456 789"
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#E8E8E8' }}>Emergency Contact</Label>
                    <Input 
                      value={formData.agency_emergency_contact} 
                      onChange={(e) => setFormData({ ...formData, agency_emergency_contact: e.target.value })} 
                      placeholder="+32 987 654 321"
                      style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                    />
                  </div>
                </div>
              </div>
              
              {/* Service Hours Selection - Only show when editing flights with Basic Assistance */}
              {editingFlight && currentBasicAssistanceService && !isAgency && (
                <div className="space-y-2" style={{ background: 'rgba(96, 165, 250, 0.1)', padding: '16px', borderRadius: '8px', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                  <Label style={{ color: '#E8E8E8', fontWeight: 'bold' }}>Basic Assistance Service Hours</Label>
                  <p className="text-xs" style={{ color: '#B0B0B0' }}>
                    Current: {currentBasicAssistanceService.service_name}
                  </p>
                  <p className="text-xs" style={{ color: '#B0B0B0' }}>
                    Price: €{currentBasicAssistanceService.price.toFixed(2)}
                    {currentBasicAssistanceService.parking_fee > 0 && (
                      <span> + Parking: €{currentBasicAssistanceService.parking_fee.toFixed(2)} = €{(currentBasicAssistanceService.price + currentBasicAssistanceService.parking_fee).toFixed(2)}</span>
                    )}
                  </p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="serviceHours"
                        value="2"
                        checked={serviceHoursSelection === 2}
                        onChange={() => handleServiceHoursChange(2)}
                        className="w-4 h-4"
                        style={{ accentColor: '#60A5FA' }}
                      />
                      <span style={{ color: '#E8E8E8' }}>2 hours</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="serviceHours"
                        value="3"
                        checked={serviceHoursSelection === 3}
                        onChange={() => handleServiceHoursChange(3)}
                        className="w-4 h-4"
                        style={{ accentColor: '#60A5FA' }}
                      />
                      <span style={{ color: '#E8E8E8' }}>3 hours</span>
                    </label>
                  </div>
                  <p className="text-xs" style={{ color: '#FFD700' }}>
                    Changing service hours will automatically update the price
                  </p>
                </div>
              )}
              
              <Button type="submit" className="w-full" style={{ background: '#FFFFFF', color: '#0A1128' }}>
                {editingFlight ? "Update Flight" : "Create Flight"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
        
        {/* O/0 Confirmation Dialog */}
        <Dialog open={prefixConfirmDialog.open} onOpenChange={(open) => !open && setPrefixConfirmDialog({ open: false, matches: [] })}>
          <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#FFD700' }}>⚠️ Airline Code Confirmation</DialogTitle>
              <DialogDescription style={{ color: '#B0B0B0' }}>
                We detected potential confusion between "O" and "0" in your flight prefix. Please select the correct airline:
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {prefixConfirmDialog.matches.map((match, idx) => (
                <Button
                  key={idx}
                  onClick={() => handlePrefixConfirm(match)}
                  className="w-full justify-between"
                  style={{ 
                    background: match.is_original ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                    border: `1px solid ${match.is_original ? '#22C55E' : '#3B82F6'}`,
                    color: '#E8E8E8'
                  }}
                >
                  <span>
                    <strong style={{ color: match.is_original ? '#22C55E' : '#3B82F6' }}>
                      {match.prefix}
                    </strong>
                    {' - ' + match.airline}
                  </span>
                  {match.is_original && <span style={{ color: '#22C55E' }}>✓ Your input</span>}
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      {/* Date Range Filter Buttons */}
      <div className="mb-4 flex gap-2 items-center">
        <Button
          variant={dateFilter === "upcoming" ? "default" : "outline"}
          onClick={() => {
            setDateFilter("upcoming");
            setSelectedMonth("all");
          }}
          style={dateFilter === "upcoming" ? 
            { background: '#22C55E', color: '#FFFFFF' } : 
            { borderColor: '#4A5A8A', color: '#E8E8E8', background: 'transparent' }
          }
        >
          Upcoming Flights
        </Button>
        <Button
          variant={dateFilter === "all" ? "default" : "outline"}
          onClick={() => {
            setDateFilter("all");
            setSelectedMonth("all");
          }}
          style={dateFilter === "all" ? 
            { background: '#60A5FA', color: '#FFFFFF' } : 
            { borderColor: '#4A5A8A', color: '#E8E8E8', background: 'transparent' }
          }
        >
          All Flights
        </Button>
        <Button
          variant={dateFilter === "past" ? "default" : "outline"}
          onClick={() => {
            setDateFilter("past");
            setSelectedMonth("all");
          }}
          style={dateFilter === "past" ? 
            { background: '#9CA3AF', color: '#FFFFFF' } : 
            { borderColor: '#4A5A8A', color: '#E8E8E8', background: 'transparent' }
          }
        >
          Past Flights
        </Button>
        
        {/* Sort Order Toggle Button */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#B0B0B0', fontSize: '14px' }}>Sort:</span>
          <Button
            variant="outline"
            onClick={() => {
              setSortOrder(sortOrder === "desc" ? "asc" : "desc");
            }}
            style={{ 
              borderColor: '#4A5A8A', 
              color: '#FFD700', 
              background: 'rgba(255, 215, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 'bold'
            }}
          >
            {sortOrder === "desc" ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m3 16 4 4 4-4"/>
                  <path d="M7 20V4"/>
                  <path d="m21 8-4-4-4 4"/>
                  <path d="M17 4v16"/>
                </svg>
                Newest First
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m3 8 4-4 4 4"/>
                  <path d="M7 4v16"/>
                  <path d="m21 16-4 4-4-4"/>
                  <path d="M17 20V4"/>
                </svg>
                Oldest First
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filter Controls */}
      <Card className="mb-6" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Year</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Years</SelectItem>
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const years = [];
                    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
                      years.push(<SelectItem key={i} value={i.toString()}>{i}</SelectItem>);
                    }
                    return years;
                  })()}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label style={{ color: '#E8E8E8' }}>Month</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Months</SelectItem>
                  <SelectItem value="1">January</SelectItem>
                  <SelectItem value="2">February</SelectItem>
                  <SelectItem value="3">March</SelectItem>
                  <SelectItem value="4">April</SelectItem>
                  <SelectItem value="5">May</SelectItem>
                  <SelectItem value="6">June</SelectItem>
                  <SelectItem value="7">July</SelectItem>
                  <SelectItem value="8">August</SelectItem>
                  <SelectItem value="9">September</SelectItem>
                  <SelectItem value="10">October</SelectItem>
                  <SelectItem value="11">November</SelectItem>
                  <SelectItem value="12">December</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Agency Filter - Hide for agency users */}
            {!isAgency && (
              <div>
                <Label style={{ color: '#E8E8E8' }}>Agency</Label>
                <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                  <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue placeholder="Select Agency" />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    <SelectItem value="all">All Agencies</SelectItem>
                    {Array.isArray(agencies) && agencies
                      .filter(agency => agency && agency.id && agency.name && typeof agency.name === 'string')
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(agency => (
                        <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-end">
              <Button 
                onClick={() => {
                  const currentDate = new Date();
                  setSelectedYear(currentDate.getFullYear().toString());
                  setSelectedMonth((currentDate.getMonth() + 1).toString());
                  setSelectedAgency("all");
                  setDateFilter("upcoming");
                }}
                variant="outline"
                style={{ borderColor: '#4A5A8A', color: '#E8E8E8', width: '100%' }}
              >
                Reset Filters
              </Button>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm" style={{ color: '#FFD700' }}>
              Showing {filteredFlights.length} flight{filteredFlights.length !== 1 ? 's' : ''}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {filteredFlights.map((flight) => (
          <Card key={flight.id} className="card-hover" style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Plane className="h-6 w-6" style={{ color: '#FFFFFF' }} />
                  <div>
                    <a 
                      href={`https://planefinder.net/flight/${flight.flight_number.toUpperCase()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xl hover:underline"
                      style={{ color: '#60A5FA' }}
                    >
                      {flight.flight_number}
                    </a>
                    <div className="text-sm font-normal" style={{ color: '#B0B0B0' }}>
                      {formatDate(flight.flight_date)} {formatTime(flight.flight_time)}
                    </div>
                    {flight.agency_name && (
                      <div className="text-sm font-medium mt-1" style={{ color: '#FFD700' }}>
                        {flight.agency_name}
                      </div>
                    )}
                    {flight.invoice_number && (
                      <div className="text-sm font-medium mt-1" style={{ color: flight.invoice_number.startsWith('NC') ? '#F59E0B' : '#10B981' }}>
                        {flight.invoice_number.startsWith('NC') ? '📋 Credit Note:' : '📄 Invoice:'} {flight.invoice_number}
                      </div>
                    )}
                    {flight.created_by_name && (
                      <div className="text-xs mt-1" style={{ color: '#A0A0A0' }}>
                        Created by: {flight.created_by_name}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {/* Staff can ONLY upload files - no other buttons */}
                  {isStaff && (
                    <>
                      <input type="file" id={`file-${flight.id}`} className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(flight.id, e.target.files[0])} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
                      <Button variant="outline" size="sm" onClick={() => document.getElementById(`file-${flight.id}`).click()} style={{ borderColor: '#FFFFFF', color: '#FFFFFF' }}>
                        <Upload className="h-4 w-4 mr-1" />Upload
                      </Button>
                    </>
                  )}
                  
                  {/* Admin and users with permissions see all buttons */}
                  {!isStaff && (
                    <>
                      {canEditFlights && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            if (isFlightLocked(flight)) {
                              const reason = (flight.invoiced || flight.invoice_number || flight.invoice_id || flight.invoice_status) 
                                ? `Flight locked - Invoice exists${flight.invoice_number ? ` (#${flight.invoice_number})` : ''}. Delete the invoice or issue a credit note to make changes.`
                                : "No changes allowed for flights before November 2025";
                              toast.error(reason);
                            } else {
                              handleEditFlight(flight);
                            }
                          }} 
                          style={{ 
                            borderColor: isFlightLocked(flight) ? '#6B7280' : '#60A5FA', 
                            color: isFlightLocked(flight) ? '#6B7280' : '#60A5FA',
                            cursor: isFlightLocked(flight) ? 'not-allowed' : 'pointer',
                            opacity: isFlightLocked(flight) ? 0.5 : 1
                          }}
                        >
                          {isFlightLocked(flight) ? '🔒 Locked' : 'Edit'}
                        </Button>
                      )}
                      {canEditFlights && !isFlightLocked(flight) && (
                        <>
                          <input type="file" id={`file-${flight.id}`} className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(flight.id, e.target.files[0])} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
                          <Button variant="outline" size="sm" onClick={() => document.getElementById(`file-${flight.id}`).click()} style={{ borderColor: '#FFFFFF', color: '#FFFFFF' }}>
                            <Upload className="h-4 w-4 mr-1" />Upload
                          </Button>
                        </>
                      )}
                      {canEditFlights && !isFlightLocked(flight) && (
                        <Button variant="outline" size="sm" onClick={() => { setSelectedFlight(flight); setServiceDialogOpen(true); }} style={{ borderColor: '#FFFFFF', color: '#FFFFFF' }}>
                          <Plus className="h-4 w-4 mr-1" />Service
                        </Button>
                      )}
                      {canEditFlights && !isFlightLocked(flight) && !isAgency && (
                        <Button variant="outline" size="sm" onClick={() => handleRefreshPrice(flight.id)} style={{ borderColor: '#FFD700', color: '#FFD700' }}>
                          <RefreshCw className="h-4 w-4 mr-1" />Refresh Price
                        </Button>
                      )}
                      {canDeleteFlights && canDeleteFlight(flight) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteFlight(flight.id)} 
                          style={{ color: '#FF6B6B' }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      {canDeleteFlights && !canDeleteFlight(flight) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          disabled
                          title={`Flight locked - Invoice exists${flight.invoice_number ? ` (#${flight.invoice_number})` : ''}. Delete the invoice to enable deletion.`}
                          style={{ color: '#6B7280', cursor: 'not-allowed', opacity: 0.5 }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Airline</div>
                  <div className="font-medium" style={{ color: '#E8E8E8' }}>{flight.airline || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Route</div>
                  <div className="font-medium" style={{ color: '#E8E8E8' }}>{flight.departure_airport || 'N/A'} → {flight.arrival_airport || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Type</div>
                  <div className="font-medium capitalize" style={{ color: '#E8E8E8' }}>{flight.flight_type}</div>
                </div>
                <div>
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Passengers</div>
                  <div 
                    className="font-medium cursor-pointer hover:underline flex items-center gap-1" 
                    style={{ color: '#60A5FA' }}
                    onClick={() => handleExportPassengerList(flight.id)}
                    title="Click to export passenger list PDF"
                  >
                    {flight.passenger_count}
                    <FileText className="h-3 w-3" />
                  </div>
                </div>
                {flight.actual_passenger_count && (
                  <div>
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>Actual PAX</div>
                    <div className="font-medium" style={{ color: '#FFD700' }}>{flight.actual_passenger_count}</div>
                  </div>
                )}
                {flight.actual_departure_time && (
                  <div>
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>Actual Dep.</div>
                    <div className="font-medium text-sm" style={{ color: '#FFD700' }}>
                      {formatLocalTime(flight.actual_departure_time)}
                    </div>
                  </div>
                )}
                {/* Hide total price from staff completely */}
                {!isStaff && (
                  <div>
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>Total</div>
                    <div className="font-medium" style={{ color: '#FFFFFF' }}>€{flight.total_amount?.toFixed(2) || '0.00'}</div>
                  </div>
                )}
              </div>
              
              {/* VAT Notice - Hide from staff completely */}
              {!isStaff && (
                <div className="mt-3 p-3 space-y-1" style={{ background: 'rgba(255, 215, 0, 0.1)', borderRadius: '6px', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                  <p className="text-xs font-semibold" style={{ color: '#FFD700' }}>VAT Information:</p>
                  <p className="text-xs leading-relaxed" style={{ color: '#E8E8E8' }}>
                    All prices are exempt Belgian VAT. Intracommunity companies are not subject to VAT. 
                    Exemptions envisaged by Article 44 of the Belgian VAT Code.
                  </p>
                  <p className="text-xs font-medium mt-1" style={{ color: '#FFD700' }}>
                    For Belgian Companies: 21% VAT will be added to invoice.
                  </p>
                </div>
              )}
              
              {flight.pnr_list && flight.pnr_list.length > 0 && (
                <div className="mb-2">
                  <span className="text-sm" style={{ color: '#B0B0B0' }}>PNRs: </span>
                  <span className="text-sm" style={{ color: '#E8E8E8' }}>{flight.pnr_list.join(', ')}</span>
                </div>
              )}
              
              {/* Operational Report Data */}
              {(flight.no_show_list?.length > 0 || flight.go_show_list?.length > 0 || flight.gate_number || flight.operational_notes) && (
                <div className="mt-3 pt-3 space-y-2" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)' }}>
                  <div className="text-sm font-semibold" style={{ color: '#FFD700' }}>Report Data:</div>
                  {flight.gate_number && (
                    <div>
                      <span className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>Gate: </span>
                      <span className="text-sm" style={{ color: '#E8E8E8' }}>{flight.gate_number}</span>
                    </div>
                  )}
                  {flight.no_show_list && flight.no_show_list.length > 0 && (
                    <div>
                      <span className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>No-Shows: </span>
                      <span className="text-sm" style={{ color: '#f87171' }}>{flight.no_show_list.join(', ')}</span>
                    </div>
                  )}
                  {flight.go_show_list && flight.go_show_list.length > 0 && (
                    <div>
                      <span className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>Go-Shows: </span>
                      <span className="text-sm" style={{ color: '#4ade80' }}>{flight.go_show_list.join(', ')}</span>
                    </div>
                  )}
                  {flight.operational_notes && (
                    <div>
                      <span className="text-sm font-semibold" style={{ color: '#B0B0B0' }}>Notes: </span>
                      <span className="text-sm" style={{ color: '#E8E8E8' }}>{flight.operational_notes}</span>
                    </div>
                  )}
                </div>
              )}
              
              {flight.services && flight.services.length > 0 && (
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(74, 90, 138, 0.3)' }}>
                  <div className="text-sm font-medium mb-2" style={{ color: '#E8E8E8' }}>Services:</div>
                  <div className="space-y-2">
                    {flight.services.map((service, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                        <div>
                          <div className="font-medium" style={{ color: '#E8E8E8' }}>{service.service_name}</div>
                          {service.assigned_staff && <div className="text-xs" style={{ color: '#B0B0B0' }}>Agent: {service.assigned_staff}</div>}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Hide prices from staff completely */}
                          {!isStaff && (
                            <div>
                              <div className="font-medium" style={{ color: '#FFFFFF' }}>€{service.price?.toFixed(2)}</div>
                              {service.parking_fee > 0 && (
                                <div className="text-xs" style={{ color: '#FBBF24' }}>+€{service.parking_fee?.toFixed(2)} parking</div>
                              )}
                            </div>
                          )}
                          {canDeleteFlights && !isStaff && (
                            canModifyService(flight) ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleRemoveService(flight.id, service.service_id)} 
                                style={{ color: '#FF6B6B', padding: '4px' }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                disabled
                                title={(flight.invoiced || flight.invoice_status || flight.invoice_number || flight.invoice_id) 
                                  ? `Flight locked - Invoice exists${flight.invoice_number ? ` (#${flight.invoice_number})` : ''}. Delete the invoice to modify services.`
                                  : "Contact us to modify services within 24 hours"}
                                style={{ color: '#6B7280', padding: '4px', cursor: 'not-allowed', opacity: 0.5 }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {flight.files && flight.files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {flight.files.map((file, idx) => (
                    <div key={idx} className="text-xs p-2 rounded flex items-center gap-2" style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#FFFFFF' }}>
                      <FileText className="h-3 w-3" />
                      <a 
                        href={`${API}/flights/${flight.id}/files/${file.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDownloadFile(flight.id, file.id, file.filename);
                        }}
                      >
                        {file.filename}
                      </a>
                      <button 
                        onClick={() => handleDeleteFile(flight.id, file.id)}
                        className="ml-1 hover:text-red-400"
                        style={{ color: '#FF6B6B' }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {flights.length === 0 && (
        <div className="text-center py-12">
          <Plane className="h-12 w-12 mx-auto mb-4" style={{ color: '#4A5A8A' }} />
          <p style={{ color: '#B0B0B0' }}>No flights found.</p>
        </div>
      )}

      <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Add Service to Flight</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              {selectedFlight && `Adding service to ${selectedFlight.flight_number}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddService} className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Service *</Label>
              <Select value={serviceFormData.service_id} onValueChange={(value) => {
                const service = services.find(s => s.id === value);
                setServiceFormData({ 
                  ...serviceFormData, 
                  service_id: value, 
                  price: service?.default_price.toString() || "",
                  parking_fee: service?.parking_fee?.toString() || "0"
                });
              }}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  {services
                    .filter(service => {
                      // Filter by flight year (match service valid_until with flight date year)
                      if (selectedFlight && selectedFlight.flight_date) {
                        // Parse flight date properly (handles DD/MM/YYYY format)
                        const flightDate = parseDateString(selectedFlight.flight_date);
                        const flightYear = flightDate ? flightDate.getFullYear() : new Date().getFullYear();
                        
                        if (service.valid_until) {
                          const serviceYear = new Date(service.valid_until).getFullYear();
                          if (serviceYear !== flightYear) {
                            return false; // Service not valid for this year
                          }
                        }
                        // Services without valid_until are available for all years
                      }
                      
                      // Filter by airport
                      if (!service.airport_codes || service.airport_codes.length === 0) {
                        // Available at all airports - no filter needed
                      } else {
                        const airportMatch = selectedFlight && service.airport_codes.includes(selectedFlight.departure_airport);
                        if (!airportMatch) return false;
                      }
                      
                      // For agency users, only show Extra Services (not Basic Assistance)
                      if (isAgency) {
                        // Filter out Basic Assistance services for agencies
                        if (service.category === "Basic Assistance") {
                          return false;
                        }
                        // Show services that are: 1) not agency-specific OR 2) for their agency
                        return !service.agency_id || service.agency_id === currentUser.agency_id;
                      }
                      
                      return true;
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name} (€{service.default_price})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Price (€) *</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={serviceFormData.price} 
                  onChange={(e) => setServiceFormData({ ...serviceFormData, price: e.target.value })} 
                  required 
                  readOnly={isAgency}
                  disabled={isAgency}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', cursor: isAgency ? 'not-allowed' : 'text' }} 
                />
                {isAgency && <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Price is set by admin</p>}
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Parking Fee (€)</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={serviceFormData.parking_fee} 
                  onChange={(e) => setServiceFormData({ ...serviceFormData, parking_fee: e.target.value })} 
                  readOnly={isAgency}
                  disabled={isAgency}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', cursor: isAgency ? 'not-allowed' : 'text' }} 
                />
                {isAgency && <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Fee is set by admin</p>}
              </div>
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Notes</Label>
              <Textarea value={serviceFormData.notes} onChange={(e) => setServiceFormData({ ...serviceFormData, notes: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
            </div>
            <Button type="submit" className="w-full" style={{ background: '#FFFFFF', color: '#0A1128' }}>Add Service</Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Contact Popup for 48-hour service restriction and 24-hour file upload restriction */}
      <Dialog open={showContactPopup} onOpenChange={setShowContactPopup}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: 'rgba(74, 90, 138, 0.4)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={24} />
              Modification Restriction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p style={{ color: '#E8E8E8', fontSize: '1.1rem', lineHeight: '1.6' }}>
              No modifications (editing flight details, deleting flights, adding/removing services, or uploading files) are allowed less than 24 hours before flight departure.
            </p>
            <p style={{ color: '#E8E8E8' }}>
              Please contact our handler at:
            </p>
            <div className="p-4 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <p className="font-mono text-lg text-center" style={{ color: '#FFD700' }}>
                {companyEmail}
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => setShowContactPopup(false)}
              style={{ background: '#FFD700', color: '#0A1128' }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      


      {/* Import Instructions Dialog */}
      <Dialog open={showImportInstructions} onOpenChange={setShowImportInstructions}>
        <DialogContent className="max-w-2xl" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700', fontSize: '1.5rem' }}>
              <FileText className="inline mr-2" />
              Import Flights from Excel
            </DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Follow these steps to import flights successfully
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1 */}
            <div className="p-4 rounded" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#22C55E' }}>
                <span className="flex items-center justify-center w-6 h-6 rounded-full" style={{ background: '#22C55E', color: '#FFFFFF', fontSize: '0.9rem' }}>1</span>
                Download Template
              </h3>
              <p className="text-sm mb-3" style={{ color: '#E8E8E8' }}>
                Download the CSV template with correct format and sample data (opens in Excel)
              </p>
              <Button
                onClick={handleDownloadTemplate}
                style={{ background: '#22C55E', color: '#FFFFFF' }}
              >
                <Download className="mr-2 h-4 w-4" />
                Download CSV Template
              </Button>
            </div>

            {/* Step 2 */}
            <div className="p-4 rounded" style={{ background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
              <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#60A5FA' }}>
                <span className="flex items-center justify-center w-6 h-6 rounded-full" style={{ background: '#60A5FA', color: '#FFFFFF', fontSize: '0.9rem' }}>2</span>
                Required Columns
              </h3>
              <div className="text-sm space-y-1" style={{ color: '#E8E8E8' }}>
                <p>• <strong>Flight Number</strong> - Example: SN1234 (airline auto-recognized)</p>
                <p>• <strong>Flight Date</strong> - Format: YYYY-MM-DD (2024-12-25)</p>
                <p>• <strong>Flight Time</strong> - Format: HH:MM (14:30)</p>
                <p>• <strong>Departure Airport</strong> - 3-letter code (BRU)</p>
                <p>• <strong>Arrival Airport</strong> - 3-letter code (LHR)</p>
                <p>• <strong>PNR List</strong> - Passenger booking references</p>
                <p>• <strong>Flight Type</strong> - "departure" or "arrival"</p>
                <p>• <strong>Passenger Count</strong> - Number of passengers</p>
                <p>• <strong>Special Requests</strong> - Any special requirements (optional)</p>
                <p>• <strong>2h Service / 3h Service</strong> - Mark with "x" for the service you want</p>
                <p className="text-xs mt-2" style={{ color: '#B0B0B0' }}>Note: Agency ID and Airline are automatically detected</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="p-4 rounded" style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#F59E0B' }}>
                <span className="flex items-center justify-center w-6 h-6 rounded-full" style={{ background: '#F59E0B', color: '#FFFFFF', fontSize: '0.9rem' }}>3</span>
                Important Notes
              </h3>
              <div className="text-sm space-y-2" style={{ color: '#E8E8E8' }}>
                <p>⚠️ All imported flights will be set as <strong>PENDING</strong> for admin approval</p>
                <p>✅ Remove sample rows before importing your data</p>
                <p>✅ Make sure all dates are in the future</p>
                <p>✅ Use valid 3-letter airport codes (BRU, LHR, etc.)</p>
                <p>✅ Mark ONLY ONE service per flight (2h OR 3h) with "x"</p>
                <p>✅ Service selection depends on your agency permissions</p>
              </div>
            </div>

            {/* Ready Button */}
            <div className="flex gap-3">
              <Button
                className="flex-1"
                onClick={() => {
                  setShowImportInstructions(false);
                  setImportDialogOpen(true);
                }}
                style={{ background: '#FFD700', color: '#0A1128', fontSize: '1rem', padding: '1.5rem' }}
              >
                I'm Ready to Import
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowImportInstructions(false)}
                style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* GDPR File Upload Popup */}
      <Dialog open={showGDPRPopup} onOpenChange={setShowGDPRPopup}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: 'rgba(74, 90, 138, 0.4)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={24} />
              GDPR Notice - File Upload
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p style={{ color: '#E8E8E8', fontSize: '1.1rem', lineHeight: '1.6' }}>
              As per GDPR rules, uploaded files will be automatically deleted 3 days after flight departure.
            </p>
            <div className="p-4 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <p style={{ color: '#FFD700', fontSize: '0.9rem' }}>
                ℹ️ This ensures compliance with data protection regulations and privacy standards.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                className="flex-1"
                onClick={handleGDPRCancel}
                variant="outline"
                style={{ borderColor: '#EF4444', color: '#EF4444' }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleGDPRAccept}
                style={{ background: '#22C55E', color: '#FFFFFF' }}
              >
                Accept & Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      
      {/* Pending Approval Notification Popup */}
      <Dialog open={showPendingApprovalPopup} onOpenChange={setShowPendingApprovalPopup}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#F59E0B' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#F59E0B' }}>
              <AlertCircle className="h-6 w-6" />
              Flight Created - Pending Approval
            </DialogTitle>
            <DialogDescription style={{ color: '#E8E8E8' }}>
              Your flight has been created successfully but requires admin approval before it will appear in your planning view.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded" style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              <h3 className="font-semibold mb-2" style={{ color: '#E8E8E8' }}>What happens next?</h3>
              <ul className="space-y-2 text-sm" style={{ color: '#B0B0B0' }}>
                <li>• Your flight is now in the pending queue</li>
                <li>• An admin will review and approve/reject it</li>
                <li>• Once approved, it will appear in your Global Planning</li>
                <li>• You can track its status in your Dashboard</li>
              </ul>
            </div>
            
            <div className="p-4 rounded" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <h3 className="font-semibold mb-2" style={{ color: '#22C55E' }}>After Approval:</h3>
              <ul className="space-y-2 text-sm" style={{ color: '#B0B0B0' }}>
                <li>• You will be able to view the Basic Assistance service price</li>
                <li>• You can add extra services to your flight</li>
                <li>• Your flight will be fully visible in Global Planning</li>
              </ul>
            </div>
            
            <Button
              className="w-full"
              onClick={() => setShowPendingApprovalPopup(false)}
              style={{ background: '#F59E0B', color: '#FFFFFF' }}
            >
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      
      {/* 2h/3h Basic Assistance Service Selection Popup */}
      <Dialog open={showServiceHoursPopup} onOpenChange={setShowServiceHoursPopup}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>Choose Assistance Type</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Select the service duration for your flight
              {['AMS', 'RTM', 'MST'].includes(pendingFlightDepartureAirport) && (
                <span className="block mt-2 text-orange-400 font-semibold">
                  Note: For {pendingFlightDepartureAirport} airport, only 3-hour service is available. If you select 2h, the system will automatically use the 3h service.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button
              className="w-full h-20 text-lg"
              onClick={() => handleServiceHoursSelection(2)}
              style={{ background: '#60A5FA', color: '#FFFFFF' }}
            >
              2 Hours Service
              {['AMS', 'RTM', 'MST'].includes(pendingFlightDepartureAirport) && (
                <span className="block text-sm opacity-75">(Will use 3h for {pendingFlightDepartureAirport})</span>
              )}
            </Button>
            <Button
              className="w-full h-20 text-lg"
              onClick={() => handleServiceHoursSelection(3)}
              style={{ background: '#22C55E', color: '#FFFFFF' }}
            >
              3 Hours Service
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Selection Dialog */}
      <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Select Service & Price</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Multiple pricing options available. Choose the service duration:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {priceOptions.map((option) => (
              <Button
                key={option.service_id}
                variant="outline"
                className="w-full h-auto py-4 flex justify-between items-center"
                onClick={() => refreshWithService(selectedFlight, option.service_id)}
                style={{ 
                  borderColor: '#4A5A8A', 
                  background: 'rgba(74, 90, 138, 0.2)',
                  color: '#E8E8E8'
                }}
              >
                <div className="flex flex-col items-start text-left">
                  <span className="font-semibold text-lg" style={{ color: '#FFFFFF' }}>
                    {option.service_time} - {option.service_name}
                  </span>
                  <span className="text-sm" style={{ color: '#B0B0B0' }}>
                    Service: €{option.unit_price.toFixed(2)} + Parking: €{option.parking_fee.toFixed(2)}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold" style={{ color: '#FFD700' }}>
                    €{option.total_price.toFixed(2)}
                  </div>
                  <div className="text-xs" style={{ color: '#B0B0B0' }}>
                    Total
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Flights;
